import { useState } from "react";
import ReactGA from "react-ga4";
import { updateNoteAction, updateGroupNoteAction } from "store/actions/notes";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useLogout, useCurrentUser } from "utils/hooks";
import { formatInput } from "utils/helpers";
import useFetchNotes from "./useFetchNotes";
import { Note } from "types";

interface UseUpdateNoteProps {
  note: Note | null;
}
interface NoteArgs {
  date: string;
  body: string;
  referenceUrl?: string;
  category: { value: string; label: string };
  mailing: boolean;
}

const useUpdateNote = ({ note }: UseUpdateNoteProps) => {
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { fetchNotes, fetchGroupNotes } = useFetchNotes();
  const { user } = useCurrentUser();

  const submitUpdateNote = (args: NoteArgs) => {
    if (!args || !note) {
      return null;
    }

    setLoadingUpdate(true);

    return new Promise(() => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                updateNote(note: {
                  date: "${args.date}",
                  body: "${formatInput(args.body)}",
                  referenceUrl: "${formatInput(args.referenceUrl ?? "")}",
                  uuid: "${note.uuid}",
                  category: "${args.category.value}",
                  mailing: ${args.mailing ? true : false}
                }) {
                  uuid
                  date
                  body
                  referenceUrl
                  patientId
                  createdAt
                  updatedAt
                  category
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "update_notes",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                setLoadingUpdate(false);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                setLoadingUpdate(false);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              setLoadingUpdate(false);
              return true;
            } else {
              fetchNotes();
              toast("Note Updated");
              setIsUpdateSuccessful(true);
              ReactGA.event("note_updated_success", {
                user_id: user?.id,
                note_id: note.uuid,
              });
              setLoadingUpdate(false);
              return dispatch(updateNoteAction(resData.data.updateNote));
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  const submitUpdateGroupNote = (args: NoteArgs) => {
    if (!args || !note) {
      return null;
    }

    setLoadingUpdate(true);

    return new Promise(() => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                updateNote(note: {
                  date: "${args.date}",
                  body: "${formatInput(args.body)}",
                  referenceUrl: "${formatInput(args.referenceUrl ?? "")}",
                  uuid: "${note.uuid}",
                  category: "${args.category.value}",
                  mailing: ${args.mailing}
                }) {
                  uuid
                  date
                  body
                  referenceUrl
                  groupId
                  createdAt
                  updatedAt
                  category
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "update_notes",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                setLoadingUpdate(false);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                setLoadingUpdate(false);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              setLoadingUpdate(false);
              return true;
            } else {
              note.groupId && fetchGroupNotes(note.groupId);
              toast("Note Updated");
              setIsUpdateSuccessful(true);
              ReactGA.event("note_updated_success", {
                user_id: user?.id,
                note_id: note.uuid,
                group_id: note.groupId,
              });
              setLoadingUpdate(false);
              return dispatch(updateGroupNoteAction(resData.data.updateNote));
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    submitUpdateNote,
    submitUpdateGroupNote,
    loadingUpdate,
    isUpdateSuccessful,
  };
};

export default useUpdateNote;
